import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSafeExecute } from '../safeExecute';
import { useAuth } from '../../../session/InternalAuthProvider';
import {
  AccountData,
  CustomErrorCode,
  EmployeeAssignmentExceptionResponse,
  PendingOrderBlockingExceptionResponse,
  ResourceAssignmentEditExceptionResponse,
  TimeAvailabilityDisabledReason,
} from '../../../api';
import { getDateFormat, getTimeFormat } from '../../getDateAndTimeFormat';
import { useLanguage } from '../../GeneralUtils';
import { getOrderPages } from '../../../utils/AccountUtils';

// A slot that no longer fits comes back as a 409 whose detail ends with
// "Reason: TimeAvailabilityDisabledReason.<reason>" (or the bare value).
const slotUnavailableReason = (detail: unknown): TimeAvailabilityDisabledReason | undefined => {
  const match = typeof detail === 'string' ? /Reason: (?:\w+\.)?(\w+)/.exec(detail) : null;
  return Object.values(TimeAvailabilityDisabledReason).find((reason) => reason === match?.[1]);
};

export const useAssignmentConflictHandler = () => {
  const executeSafely = useSafeExecute();
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const language = useLanguage();

  const executeWithConflictCheck = async <T,>(
    operation: () => Promise<T>,
    successMessage: string,
    finallyCallback?: () => void,
    suppressSuccessMessage?: boolean,
  ): Promise<T> => {
    return executeSafely({
      apiCall: operation,
      successMessage,
      suppressSuccessMessage,
      onErrorMessageGenerator: (error) => {
        if (error.body?.detail === 'startTimeCollisionOnWeekday') {
          return t('eventSchedule.forwardEdit.collisionError');
        }
        if (error.body.error_code === CustomErrorCode.EMPLOYEE_ASSIGNMENT_EXCEPTION) {
          const orderPages = getOrderPages(account);
          const errorResponse = error.body as EmployeeAssignmentExceptionResponse;
          return t('calendar.edit_event.error_employee_assignment', {
            conflictingDate: moment(errorResponse.conflicting_date).format(shortDateFormat),
            conflictingTime: moment(errorResponse.conflicting_time).format(getTimeFormat(account)),
            orderPage: orderPages?.find((x) => x.id === errorResponse.order_page_id)?.label[language],
            orderNumber: errorResponse.order_number,
          });
        }
        if (error.body.error_code === CustomErrorCode.RESOURCE_ASSIGNMENT_EXCEPTION) {
          const orderPages = getOrderPages(account);
          const errorResponse = error.body as ResourceAssignmentEditExceptionResponse;

          const hasConflictingOrders = errorResponse.conflicting_order_numbers?.length > 0;
          const hasConflictingUnavailabilities = errorResponse.conflicting_unavailabilities?.length > 0;

          const baseParams = {
            conflictingDate: moment(errorResponse.conflicting_date).format(shortDateFormat),
            conflictingTime: moment.unix(errorResponse.conflicting_time).tz('utc').format(getTimeFormat(account)),
            orderPage: orderPages?.find((x) => x.id === errorResponse.order_page_id)?.label[language],
          };

          if (hasConflictingOrders && hasConflictingUnavailabilities) {
            // Both conflicts exist
            return t('calendar.edit_event.error_resource_assignment_both', {
              ...baseParams,
              conflictingOrderNumbers: errorResponse.conflicting_order_numbers.join(', '),
              conflictingUnavailabilities: errorResponse.conflicting_unavailabilities.join(', '),
            });
          }
          if (hasConflictingOrders) {
            // Only order conflicts
            return t('calendar.edit_event.error_resource_assignment_orders', {
              ...baseParams,
              conflictingOrderNumbers: errorResponse.conflicting_order_numbers.join(', '),
            });
          }
          if (hasConflictingUnavailabilities) {
            // Only unavailability conflicts
            return t('calendar.edit_event.error_resource_assignment_unavailabilities', {
              ...baseParams,
              conflictingUnavailabilities: errorResponse.conflicting_unavailabilities.join(', '),
            });
          }

          // Fallback to generic message if no specific conflicts
          return t('calendar.edit_event.error_resource_assignment_generic', baseParams);
        }
        if (error.body.error_code === CustomErrorCode.PENDING_ORDER_BLOCKING_EXCEPTION) {
          const orderPages = getOrderPages(account);
          const errorResponse = error.body as PendingOrderBlockingExceptionResponse;

          // Convert seconds to minutes
          const timeLeftMinutes = Math.ceil(errorResponse.payment_time_left_seconds / 60);

          return t('calendar.edit_event.error_pending_order_blocking', {
            conflictingDate: moment(errorResponse.conflicting_date).format(shortDateFormat),
            conflictingTime: moment.unix(errorResponse.conflicting_time).tz('utc').format(getTimeFormat(account)),
            orderPage: orderPages?.find((x) => x.id === errorResponse.order_page_id)?.label[language],
            pendingOrderNumbers: errorResponse.pending_order_numbers.join(', '),
            timeLeftMinutes,
          });
        }
        const reason = error.status === 409 ? slotUnavailableReason(error.body?.detail) : undefined;
        if (reason) {
          return t('calendar.edit_event.error_slot_unavailable', {
            reason: t(`calendar.edit_event.slotUnavailableReasons.${reason}`),
          });
        }
        // A scheduler edit that no longer fits comes back as a plain 400 with the reason in
        // `detail`. Showing our generic message instead would hide the only useful information
        // the admin has, so pass the server's text through untouched.
        if (error.status === 400 && typeof error.body?.detail === 'string' && error.body.detail) {
          return error.body.detail;
        }
        return t('calendar.edit_event.error');
      },
      finallyCallback,
    });
  };

  return { executeWithConflictCheck };
};
